import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseTable } from "@/shared/ui/base-table";
import type { Column } from "@/shared/ui/base-table/types";
import type { OfferCargoSubscriptionManagementProps } from "@/widgets/offer-cargo-subscription-management/model";
import type { AddSubscriptionFormSchemaType } from "@/widgets/offer-cargo-subscription-management/model";
import { AddSubscriptionForm } from "@/widgets/offer-cargo-subscription-management/ui/add-subscription-form";
import { ConfirmDeleteSubscriptionDialog } from "@/widgets/offer-cargo-subscription-management/ui/confirm-delete-subscription-dialog";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";

type SubscriptionRow = {
  id: number;
  offerCargoCompany: {
    id: number;
    name: string;
  } | null;
};

export const OfferCargoSubscriptionManagementUi = ({
  subscriptions,
  companies,
  addSubscription,
  deleteSubscription,
  isAddingSubscription,
  isDeletingSubscription,
}: OfferCargoSubscriptionManagementProps) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteDialogState, setDeleteDialogState] = useState<{
    open: boolean;
    subscriptionId: number | null;
    companyName: string | null;
  }>({
    open: false,
    subscriptionId: null,
    companyName: null,
  });

  const handleAddSubscription = useCallback(
    async (data: AddSubscriptionFormSchemaType) => {
      try {
        await addSubscription(data.companyId);
        setShowAddForm(false);
      } catch (error) {
        console.error("Failed to add subscription:", error);
      }
    },
    [addSubscription],
  );

  const handleDeleteClick = useCallback(
    (subscriptionId: number, companyName: string) => {
      setDeleteDialogState({
        open: true,
        subscriptionId,
        companyName,
      });
    },
    [],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (deleteDialogState.subscriptionId) {
      try {
        await deleteSubscription(deleteDialogState.subscriptionId);
        setDeleteDialogState({
          open: false,
          subscriptionId: null,
          companyName: null,
        });
      } catch (error) {
        console.error("Failed to delete subscription:", error);
      }
    }
  }, [deleteSubscription, deleteDialogState.subscriptionId]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogState({
      open: false,
      subscriptionId: null,
      companyName: null,
    });
  }, []);

  const subscriptionRows: SubscriptionRow[] =
    subscriptions.data?.map((subscription) => {
      const offerCargoCompany = companies.data?.find(
        (company) => company.id === subscription.offerCargoCompanyId,
      );
      if (!offerCargoCompany) {
        console.error(
          `Offer cargo company with ID ${subscription.offerCargoCompanyId} not found.`,
        );
        return {
          id: subscription.id || 0,
          offerCargoCompany: null,
        };
      }
      return {
        id: subscription.id || 0,
        offerCargoCompany,
      };
    }) ?? [];

  const columns: Column<SubscriptionRow>[] = [
    {
      key: "offerCargoCompany",
      label: "荷主企業名",
      cell: (row) => {
        return (
          <Typography>{row?.offerCargoCompany?.name ?? "エラー"}</Typography>
        );
      },
    },
    {
      key: "id",
      label: "",
      cell: (row) => {
        return (
          <IconButton
            size="small"
            color="error"
            onClick={() => {
              if (!row.offerCargoCompany) return;
              handleDeleteClick(row.id, row.offerCargoCompany.name);
            }}
            disabled={isDeletingSubscription}
          >
            <BaseIcon name="trash" />
          </IconButton>
        );
      },
    },
  ];

  return (
    <Stack spacing={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" fontWeight={600}>
          荷物通知購読管理
        </Typography>
        <BaseButton
          variant="contained"
          startIcon={<BaseIcon name="plus" />}
          onClick={() => setShowAddForm(true)}
          disabled={showAddForm}
        >
          追加
        </BaseButton>
      </Box>

      <Collapse in={showAddForm}>
        <AddSubscriptionForm
          companies={companies.status === "success" ? companies.data : null}
          onSubmit={handleAddSubscription}
          onCancel={() => setShowAddForm(false)}
          isSubmitting={isAddingSubscription}
        />
      </Collapse>

      {subscriptions.status === "success" && (
        <BaseTable
          rows={subscriptionRows}
          columns={columns}
          noDataMessage="サブスクリプションがありません。"
        />
      )}

      {subscriptions.status === "loading" && (
        <Typography>読み込み中...</Typography>
      )}

      {subscriptions.status === "error" && (
        <Typography color="error">データの読み込みに失敗しました。</Typography>
      )}

      <ConfirmDeleteSubscriptionDialog
        open={deleteDialogState.open}
        onClose={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
        companyName={deleteDialogState.companyName || undefined}
      />
    </Stack>
  );
};
